import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { TreeProps } from 'antd';
import { IAsset } from 'models/Assets';
import usePagination from 'hooks/usePagination';
import useAssetsTypes from 'hooks/useAssetsTypes';
import useProjects from 'hooks/useProjects';
import useAllAssets from 'hooks/useAllAssets';
import MapViewHooks from './MapViewHooks';
import MapViewMenu from './MapViewMenu';
import UpdateBounds from './UpdateBounds';
import MapEventsHandler from './MapEventsHandler';
import MapViewOptions from './MapViewOptions';
import PMVHooks from 'pages/PMV/PMVHooks';
import { useQuery, useSubscription } from '@apollo/client';
import { OnLastPMVMessage } from 'graphql/subscriptions/pmv_status';
import { GET_CCTV_STATUS } from 'graphql/queries/cctv_status';
import { OnLastCCTVMessage } from 'graphql/subscriptions/cctv_status';
import { OnAllWeatherStationsLastData } from 'graphql/subscriptions/weather_station_status';
import { OnAllETDLastData } from 'graphql/subscriptions/etd_status';
import useSosOnlineStatuses from 'hooks/useSosOnlineStatuses';

const MapView: React.FC = () => {
    const { getPMVStatus, formStates, hookPMVStatuses, updatePanelState } = PMVHooks();
    const { hasTwoHyphens, openMenuItem, FocusOnMarker, getMarker } = MapViewHooks();
    const [focusedPosition, setFocusedPosition] = useState<[number, number] | null>(null);
    const [bounds, setBounds] = useState<[number, number][] | null>(null);
    const [checkedKeys, setCheckedKeys] = useState<TreeProps['checkedKeys']>([]);

    const hookProjects = useProjects({ package_id: 17, pageSize: 10 });
    const hookAssetsTypes = useAssetsTypes({ package_id: 17, pageSize: 20 });
    const hookAssets = useAllAssets({ pageSize: 1000 });
    const hookAssetsSelected = usePagination<IAsset>({ pageSize: 1200 });
    const hookSosOnlineStatuses = useSosOnlineStatuses({});
    const { data: queryCCTVData } = useQuery(GET_CCTV_STATUS);
    const { data: subscriptionDataPMV } = useSubscription(OnLastPMVMessage);
    const { data: subscriptionDataCCTV } = useSubscription(OnLastCCTVMessage);
    const { data: subscriptionDataWeatherStation } = useSubscription(OnAllWeatherStationsLastData);
    const { data: subscriptionDataETD } = useSubscription(OnAllETDLastData);

    // Obtener el estado de los postes sos
    useEffect(() => {
        if (hookAssets.data.length > 0 && !hookAssets.loading && !hookSosOnlineStatuses.loading && hookSosOnlineStatuses.data.length > 0) {
            for (const sosOnlineStatus of hookSosOnlineStatuses.data) {
                const { sos_id, is_online } = sosOnlineStatus;
                const currentSos: IAsset | undefined = hookAssets.getElementById(sos_id);
                if (currentSos) {
                    hookAssets.updateElement({ ...currentSos, is_online } as IAsset);
                }
            }
        }
    }, [hookSosOnlineStatuses.loading, hookSosOnlineStatuses.data, hookAssets.data.length, hookAssets.loading]);

    // Obtener el estado de las CCTV
    useEffect(() => {
        if (hookAssets.data.length > 0 && !hookAssets.loading && queryCCTVData?.its_cctv_statuses) {
            for (const cctvStatus of queryCCTVData.its_cctv_statuses) {
                const { is_online, cctv_id } = cctvStatus;
                const currentCctv: IAsset | undefined = hookAssets.getElementById(cctv_id);
                if (currentCctv) {
                    hookAssets.updateElement({ ...currentCctv, is_online });
                }
            }
        }
    }, [queryCCTVData, hookAssets.data.length, hookAssets.loading, checkedKeys]);
    // Actualizar estado de una estación meteorológica
    useEffect(() => {
        if (subscriptionDataWeatherStation) {
            for (let index = 0; index < subscriptionDataWeatherStation.its_weather_station_statuses.length; index++) {
                const WeatherStationStatus = subscriptionDataWeatherStation.its_weather_station_statuses[index];
                const weatherStationId = WeatherStationStatus.weather_station_id;
                const currentData: IAsset | undefined = hookAssets.getElementById(weatherStationId);

                if (currentData) {
                    hookAssets.updateElement({ ...currentData, is_online: WeatherStationStatus.is_online } as IAsset);
                }
            }
        }
    }, [subscriptionDataWeatherStation]);
    // Actualizar estado de una ETD
    useEffect(() => {
        if (subscriptionDataETD) {
            for (let index = 0; index < subscriptionDataETD.its_etd_statuses.length; index++) {
                const ETDStatus = subscriptionDataETD.its_etd_statuses[index];
                const etdId = ETDStatus.weather_station_id;
                const currentData = hookAssets.getElementById(etdId);

                if (currentData) {
                    hookAssets.updateElement({ ...currentData, is_online: ETDStatus.is_online } as IAsset);
                }
            }
        }
    }, [subscriptionDataETD]);
    // Actualizar el estado de un CCTV
    useEffect(() => {
        if (subscriptionDataCCTV) {
            const cctvId = subscriptionDataCCTV.its_cctv_statuses[0].cctv_id;
            const isOnline = subscriptionDataCCTV.its_cctv_statuses[0].is_online;
            const currentData = hookAssets.getElementById(cctvId);
            hookAssets.updateElement({ ...currentData, is_online: isOnline } as IAsset);
        }
    }, [subscriptionDataCCTV]);

    // Actualizar panel si se recibe un nuevo mensaje
    useEffect(() => {
        if (subscriptionDataPMV) {
            const pmvId = subscriptionDataPMV.its_pmv_statuses[0].pmv_id;
            const pmv = hookAssets.data.find((asset) => asset.id === pmvId);
            if (pmv) {
                const message = subscriptionDataPMV.its_pmv_statuses[0].message;
                const isOnline = subscriptionDataPMV.its_pmv_statuses[0].is_online;
                updatePanelState(pmvId, message, isOnline);
            }
        }
        // console.log("🚀 ~ subscriptionDataPMV:", subscriptionDataPMV)
    }, [subscriptionDataPMV]);

    // Al obtener los elementos seleccionados se calculan los límites del mapa
    useEffect(() => {
        if (!hookAssetsSelected.loading && hookAssetsSelected.data.length > 0) {
            const assetBounds: [number, number][] = hookAssetsSelected.data
                .filter(({ latitude, longitude }) => latitude != null && longitude != null)
                .map(({ latitude, longitude }) => [latitude as number, longitude as number]);
            setBounds(assetBounds);
        }
    }, [hookAssetsSelected.loading, hookAssetsSelected.data]);

    // Obtener el mensaje actual de cada panel
    useEffect(() => {
        if (!hookAssetsSelected.loading && hookAssetsSelected.data) {
            for (let pmv of hookAssetsSelected.data) {
                getPMVStatus(pmv);
            }
        }
    }, [hookAssetsSelected.loading, hookAssetsSelected.data, hookPMVStatuses.data, checkedKeys]);

    // Al obtener los elementos se calculan los límites del mapa
    useEffect(() => {
        if (!hookAssets.loading && hookAssets.data.length > 0 && hookAssetsSelected.data.length === 0) {
            const assetBounds: [number, number][] = hookAssets.data
                .filter(({ latitude, longitude }) => latitude != null && longitude != null)
                .map(({ latitude, longitude }) => [latitude as number, longitude as number]);
            setBounds(assetBounds);
        }
    }, [hookAssets.data, hookAssets.loading, hookAssetsSelected.data.length]);

    // Al obtener los elementos seleccionados se filtran los elementos que se mostrarán en el mapa
    useEffect(() => {
        hookAssetsSelected.loadingStart();
        // Asegurarse de que checkedKeys es un array de strings
        const keysArray = Array.isArray(checkedKeys) ? checkedKeys : [];
        const checkedAssets = keysArray
            .filter(checkedKey => hasTwoHyphens(String(checkedKey)))
            .map(checkedKey => String(checkedKey).split('-')[2]);
        const filteredAssets = hookAssets.data.filter(asset => checkedAssets.includes(String(asset.id)) && asset.latitude && asset.longitude);
        hookAssetsSelected.setData(filteredAssets);
        hookAssetsSelected.loadingEnd();
    }, [checkedKeys, hookAssets.data]);

    return (
        <div style={{ position: 'relative', height: '100vh' }}>
            {/* Menú de opciones */}
            <MapViewMenu openMenuItem={openMenuItem} />
            {/* Opciones de visualización */}
            <MapViewOptions
                hookProjects={hookProjects}
                hookAssetsTypes={hookAssetsTypes}
                hookAssets={hookAssets}
                checkedKeys={checkedKeys}
                setCheckedKeys={setCheckedKeys}
            />
            {/* Mostrar el mapa cuando se obtengan los elementos que se mostrarán */}
            {bounds && (
                <MapContainer
                    zoom={12}
                    style={{ height: "100vh" }}
                >
                    {/* Actualizar los límites del mapa */}
                    {bounds && <UpdateBounds bounds={bounds} />}
                    {/* Manejador de eventos del mapa */}
                    <MapEventsHandler setFocusedPosition={setFocusedPosition} />
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                    />
                    {/* Recorrer elementos filtrados para mostrarlos en el mapa */}
                    {hookAssetsSelected.data.map((asset) => getMarker(asset, formStates, setFocusedPosition))}
                    {focusedPosition && <FocusOnMarker position={focusedPosition} />}
                </MapContainer>
            )}
        </div>
    );
};

export default MapView;